import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import type { Listing } from "@/types";
import { ADVISOR_MESSAGE_TIMEOUT_MS } from "@/lib/advisor-request";
import { parseAdvisorStreamBuffer, type AdvisorStreamEvent } from "@/lib/advisor-stream";
import { reportApiError } from "@/lib/error-reporting";
import { coerceDetailMessage } from "@/lib/errorDetail";
import type { UnmatchedTown } from "@/lib/unmatched-towns";

type RetriableConfig = InternalAxiosRequestConfig & { _csrfRetry?: boolean };
let csrfToken: string | null = null;

const PRODUCTION_API_URL = "https://openhomesai-production.up.railway.app";
const ACCESS_TOKEN_STORAGE_KEY = "homeai_access_token";

export function getStoredAccessToken() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeAccessToken(token?: string | null) {
  if (typeof window === "undefined" || !token) return false;
  try {
    window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
    return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) === token;
  } catch {
    // The auth cookie path may still work. If both storage mechanisms fail,
    // the follow-up /auth/me check will show a user-facing session error.
    return false;
  }
}

export function clearStoredAccessToken() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  } catch {}
}

function resolveApiBaseUrl() {
  const configured = process.env.NEXT_PUBLIC_API_URL?.trim();
  if (configured) return configured.replace(/\/$/, "");

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (
      hostname === "openhomesai.casa"
      || hostname === "www.openhomesai.casa"
      || hostname.endsWith(".vercel.app")
    ) {
      return PRODUCTION_API_URL;
    }
  }

  return "http://localhost:8000";
}

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: 12000,
  withCredentials: true,
});

function isUnsafeMethod(method?: string) {
  const normalized = (method || "get").toUpperCase();
  return ["POST", "PUT", "PATCH", "DELETE"].includes(normalized);
}

function isAuthBootstrapPath(url?: string) {
  return Boolean(url && ["/auth/login", "/auth/signup", "/auth/waitlist", "/auth/forgot-password", "/auth/reset-password"].includes(url));
}

async function ensureCsrfToken(force = false) {
  if (csrfToken && !force) return csrfToken;
  try {
    const response = await axios.get<{ csrf_token: string }>(`${api.defaults.baseURL}/auth/csrf`, { withCredentials: true, timeout: 12000 });
    csrfToken = response.data.csrf_token;
  } catch {
    // Anonymous users may not have an auth session; authenticated calls will surface their real error.
    csrfToken = Cookies.get("homeai_csrf") || null;
  }
  return csrfToken;
}

function clearCsrfCookie() {
  csrfToken = null;
  Cookies.remove("homeai_csrf", { path: "/" });
  Cookies.remove("homeai_csrf");
}

api.interceptors.request.use(async (config) => {
  if (isAuthBootstrapPath(config.url)) csrfToken = null;
  const bearerToken = getStoredAccessToken();
  if (bearerToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${bearerToken}`;
  }
  if (isUnsafeMethod(config.method) && !isAuthBootstrapPath(config.url) && !bearerToken) {
    const token = await ensureCsrfToken();
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  async (error: AxiosError<{ detail?: string }>) => {
    const config = error.config as RetriableConfig | undefined;

    // 401s propagate to callers — protected pages redirect themselves;
    // anonymous browsing of public pages must not trigger a forced login.
    if (error.response?.status === 401 && !isAuthBootstrapPath(config?.url)) {
      clearStoredAccessToken();
    }

    const isCsrfError = error.response?.status === 403 && error.response.data?.detail === "Invalid CSRF token";
    if (config && isCsrfError && !config._csrfRetry) {
      config._csrfRetry = true;
      clearCsrfCookie();
      const token = await ensureCsrfToken(true);
      if (token) config.headers["X-CSRF-Token"] = token;
      return api(config);
    }
    reportTransportError(error);
    return Promise.reject(error);
  },
);

function reportTransportError(error: AxiosError<{ detail?: string }>) {
  const status = error.response?.status ?? null;
  // Expected outcomes don't deserve an error report — they're not bugs.
  // 429 (rate limited) is expected too: it's the limiter working, not a failure.
  if (status === 401 || status === 403 || status === 404 || status === 409 || status === 422 || status === 429) {
    return;
  }
  const config = error.config as RetriableConfig | undefined;
  const url = config?.url ?? null;
  // Don't loop on our own ingest endpoint failing.
  if (url && url.includes("/client-errors")) return;
  reportApiError({
    status,
    method: (config?.method || "GET").toUpperCase(),
    url,
    message: error.message || error.response?.data?.detail || null,
    detail: error.response?.data ?? null,
  });
}

export default api;

export const auth = {
  betaStatus: () => api.get<{ invite_only: boolean; message: string }>("/auth/beta-status"),
  joinWaitlist: (data: { name: string; email: string; target_towns?: string; note?: string }) =>
    api.post<{ ok: boolean; message: string; already_joined?: boolean }>("/auth/waitlist", data),
  signup: (name: string, email: string, password: string) =>
    api.post<{ ok: boolean; access_token?: string; token_type?: string }>("/auth/signup", { name, email, password }),
  login: (email: string, password: string) =>
    api.post<{ ok: boolean; access_token?: string; token_type?: string }>("/auth/login", new URLSearchParams({ username: email, password }), {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }),
  me: () => api.get("/auth/me"),
  updateMe: (data: { name?: string; email?: string }) => api.patch("/auth/me", data),
  logout: () => api.post("/auth/logout"),
};

// What a typed town matches (backend services/town_match.py, the one matcher the
// query parser also uses): suggestions best first, and what a submitted search
// resolves to.
export interface TownMatch {
  town: string;
  state: string;
  kind: "exact" | "prefix" | "fuzzy";
  distance: number;
}

export type TownResolution =
  | { status: "exact" | "corrected"; town: string; state: string }
  | { status: "ambiguous"; options: TownMatch[] }
  | { status: "none" };

export interface TownMatchesResponse {
  query: string;
  matches: TownMatch[];
  resolution: TownResolution;
}

export const listings = {
  search: (filters: Record<string, string | number | boolean | undefined>) =>
    api.get("/listings/", { params: filters }),
  townMatches: (q: string) => api.get<TownMatchesResponse>("/listings/town-matches", { params: { q } }),
  get: (id: number) => api.get(`/listings/${id}`),
  score: (id: number, criteria: Record<string, unknown>) =>
    api.post(`/listings/${id}/score`, criteria),
  count: (city?: string) =>
    api.get("/listings/count", { params: city ? { city } : {} }),
  counts: (cities: string[]) =>
    api.get<{ counts: Record<string, number>; total: number }>("/listings/counts", {
      params: { cities: cities.join(",") },
    }),
  health: () => api.get("/listings/health"),
  markets: () => api.get<{ markets: Array<{ city: string; state: string }> }>("/listings/markets"),
};

export const sync = {
  runs: (params?: { city?: string; state?: string; limit?: number }) =>
    api.get("/sync/runs", { params }),
  city: (city: string, state = "NJ") =>
    api.post("/sync/dashboard/city", null, { params: { city, state } }),
  beta: () => api.post("/sync/dashboard/beta"),
};

export interface ClientErrorRecord {
  id: number;
  created_at: string | null;
  client_timestamp: string | null;
  fingerprint: string;
  error_kind: string;
  name: string | null;
  message: string | null;
  stack: string | null;
  source: string | null;
  route: string | null;
  url: string | null;
  referrer: string | null;
  user_id: number | null;
  conversation_id: number | null;
  active_listing_id: number | null;
  session_id: string | null;
  build_version: string | null;
  environment: string | null;
  user_agent: string | null;
  viewport_width: number | null;
  viewport_height: number | null;
  locale: string | null;
  context: Record<string, unknown> | null;
  resolved_at: string | null;
}

export interface ClientErrorGroupRecord {
  fingerprint: string;
  error_kind: string;
  name: string | null;
  message: string | null;
  route: string | null;
  build_version: string | null;
  environment: string | null;
  occurrences: number;
  distinct_users: number;
  distinct_sessions: number;
  first_seen: string | null;
  last_seen: string | null;
  sample_id: number;
}

export interface AdvisorPromptSection {
  name: string;
  chars: number;
  lines: number;
  items: number | null;
}

export interface AdvisorPromptMessageOut {
  role: string;
  content: string;
  chars: number;
}

export interface AdvisorPromptDebugResponse {
  prompt_version: string;
  system_prompt: string;
  system_prompt_chars: number;
  messages: AdvisorPromptMessageOut[];
  sections: AdvisorPromptSection[];
  metadata: Record<string, unknown>;
}

export interface AdvisorPromptDebugRequest {
  question: string;
  listing_id?: number | null;
  context?: string | null;
  mode?: string | null;
  surface?: string | null;
  saved_search_id?: number | null;
  watch_id?: number | null;
  conversation_id?: number | null;
  context_snapshot?: Record<string, unknown>;
}

export const adminAdvisorDebug = {
  prompt: (payload: AdvisorPromptDebugRequest) =>
    api.post<AdvisorPromptDebugResponse>("/admin/advisor/debug-prompt", payload),
};

export const adminClientErrors = {
  list: (params?: { limit?: number; fingerprint?: string; kind?: string; user_id?: number; unresolved_only?: boolean }) =>
    api.get<ClientErrorRecord[]>("/admin/client-errors", { params }),
  groups: (params?: { limit?: number; unresolved_only?: boolean }) =>
    api.get<ClientErrorGroupRecord[]>("/admin/client-errors/groups", { params }),
  resolve: (id: number) => api.post<ClientErrorRecord>(`/admin/client-errors/${id}/resolve`),
};

export interface BetaWaitlistEntryRecord {
  id: number;
  name: string;
  email: string;
  target_towns: string | null;
  note: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface BetaWaitlistInviteResponse {
  entry: BetaWaitlistEntryRecord;
  already_invited: boolean;
  email_sent: boolean;
  message: string;
}

export const adminBetaWaitlist = {
  list: (params?: { status?: string; limit?: number }) =>
    api.get<BetaWaitlistEntryRecord[]>("/admin/beta/waitlist", { params }),
  invite: (id: number) =>
    api.post<BetaWaitlistInviteResponse>(`/admin/beta/waitlist/${id}/invite`),
};

export const favorites = {
  list: () => api.get("/favorites/"),
  learningSummary: () => api.get("/favorites/learning-summary"),
  add: (listingId: number) => api.post(`/favorites/${listingId}`),
  update: (listingId: number, data: { decision_status?: "interested" | "tour" | "maybe" | "pass"; note?: string | null }) =>
    api.patch(`/favorites/${listingId}`, data),
  remove: (listingId: number) => api.delete(`/favorites/${listingId}`),
};

export type ListingPreferenceVote = "like" | "dislike" | "maybe" | "not_for_me";

export interface ListingPreferencePayload {
  vote: ListingPreferenceVote;
  reasons?: string[];
  note?: string | null;
  source?: string;
  context?: Record<string, unknown>;
}

export type KnownPreferenceProfileSignalKind =
  | "city"
  | "property_type"
  | "feature"
  | "price_band"
  | "sqft_band"
  | "bed_count"
  | "bath_count"
  | "town_quality"
  | "max_price"
  | "target_monthly_payment"
  | "lot_size"
  | "garage"
  | "basement"
  | "school_importance"
  | "commute_destination"
  | "max_commute"
  | "must_have"
  | "nice_to_have"
  | "dealbreaker"
  | "liked_listing_pattern"
  | "disliked_listing_pattern"
  | "budget_sensitivity"
  | "other";

export type PreferenceProfileSignalKind = KnownPreferenceProfileSignalKind | (string & {});

export type PreferenceSource = "explicit" | "inferred" | "search_behavior";
export type PreferenceConfidence = "low" | "medium" | "high";
export type PreferenceStatus = "must-have" | "nice-to-have" | "prefer" | "avoid" | "dealbreaker" | "neutral" | "outdated" | "dismissed";
export type PreferencePriority =
  | "must_have"
  | "nice_to_have"
  | "prefer"
  | "avoid"
  | "dealbreaker"
  | "inferred_positive"
  | "inferred_negative"
  | "neutral";
export type PreferenceCorrectionAction =
  | "remove_inferred"
  | "mark_must_have"
  | "mark_prefer"
  | "mark_nice_to_have"
  | "mark_avoid"
  | "mark_dealbreaker"
  | "mark_neutral"
  | "mark_outdated"
  | "dismiss"
  | "clear_learned_signals";

export interface PreferenceProfileSignal {
  category?: string | null;
  key?: string | null;
  kind: PreferenceProfileSignalKind;
  value: string;
  label: string;
  priority?: PreferencePriority | null;
  explicitness?: PreferenceSource | null;
  weight: number;
  source?: PreferenceSource | null;
  confidence?: PreferenceConfidence | null;
  evidence_count?: number | null;
  dismissed_at?: string | null;
  updated_at?: string | null;
  last_updated?: string | null;
  source_signals?: Record<string, unknown>[];
  status?: PreferenceStatus | null;
  response_prefix?: string | null;
}

export interface PreferenceProfileSummary {
  total_preferences: number;
  likes: PreferenceProfileSignal[];
  dislikes: PreferenceProfileSignal[];
  memory?: {
    explicit: PreferenceProfileSignal[];
    inferred: PreferenceProfileSignal[];
    // What Mae learned from the buyer's saved searches, including each AI
    // preference saved with one (kind "nice_to_have").
    search_behavior?: PreferenceProfileSignal[];
    response_rules: Record<string, string>;
  } | null;
}

export interface PreferenceCorrectionResponse {
  message: string;
  affected: number;
  affected_watches?: Array<{ id: number; name: string; reason?: string }>;
}

export interface ListingPreferenceItem {
  listing_id: number;
  vote: ListingPreferenceVote;
  reasons: string[];
  note: string | null;
  source: string;
  context: Record<string, unknown>;
  listing: Listing;
  created_at: string | null;
  updated_at: string | null;
}

export interface ListingPreferenceRecord {
  listing_id: number;
  vote: ListingPreferenceVote;
  reasons: string[];
  note: string | null;
  source: string;
  context: Record<string, unknown>;
}

export interface GeoBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface GeoBoundary {
  city: string;
  label: string;
  bounds: GeoBounds;
  polygon: number[][][];
}

export const listingPreferences = {
  get: (listingId: number) => api.get<ListingPreferenceRecord>(`/listing-preferences/${listingId}`),
  save: (listingId: number, data: ListingPreferencePayload) =>
    api.put<ListingPreferenceRecord>(`/listing-preferences/${listingId}`, data),
  remove: (listingId: number) => api.delete(`/listing-preferences/${listingId}`),
  profileSummary: () => api.get<PreferenceProfileSummary>("/listing-preferences/profile/summary"),
  profileItems: (params?: { vote?: ListingPreferenceVote; limit?: number }) =>
    api.get<ListingPreferenceItem[]>("/listing-preferences/profile/items", { params }),
  suppressProfileSignal: (data: { kind: PreferenceProfileSignalKind; value: string }) =>
    api.post("/listing-preferences/profile/suppress", data),
  correctProfileSignal: (data: { action: PreferenceCorrectionAction; kind?: PreferenceProfileSignalKind; value?: string }) =>
    api.post<PreferenceCorrectionResponse>("/listing-preferences/profile/correct", data),
  editProfileSignal: (data: {
    kind: PreferenceProfileSignalKind;
    value: string;
    new_value?: string | null;
    label?: string | null;
    action?: PreferenceCorrectionAction | null;
    note?: string | null;
  }) => api.patch<PreferenceCorrectionResponse>("/listing-preferences/profile/signal", data),
};

export type ListingInteractionType =
  | "listing_viewed"
  | "listing_saved"
  | "listing_unsaved"
  | "listing_hidden"
  | "listing_unhidden"
  | "listing_watched"
  | "listing_unwatched"
  | "listing_compared"
  | "listing_shared"
  | "advisor_question_asked"
  | "feedback_find_more_like_this"
  | "feedback_not_my_style"
  | "feedback_good_example"
  | "feedback_too_expensive"
  | "feedback_like_location"
  | "feedback_like_layout"
  | "feedback_need_more_land"
  | "feedback_too_much_uncertainty"
  | "feedback_prefer_lower_monthly_payment";

export function trackListingInteraction(
  listingId: number,
  interactionType: ListingInteractionType,
  metadata: Record<string, unknown> = {},
) {
  return api.post("/listing-interactions", {
    listing_id: listingId,
    interaction_type: interactionType,
    metadata,
  }).catch(() => undefined);
}

export type AdvisorSuggestionFeedbackCategory =
  | "price"
  | "monthly_payment"
  | "location"
  | "home_type"
  | "size"
  | "lot"
  | "style"
  | "condition"
  | "schools"
  | "commute"
  | "taxes_hoa"
  | "data_quality"
  | "layout"
  | "overall";

export interface AdvisorSuggestionFeedback {
  feedbackId?: number;
  listingId: string;
  feedbackDirection: "more_like_this" | "less_like_this";
  reasons: Array<{
    category: AdvisorSuggestionFeedbackCategory;
    reasonKey: string;
    sentiment: "positive" | "negative";
    label: string;
  }>;
  freeText?: string;
  listingSnapshot?: {
    price?: number;
    monthlyEstimate?: number;
    town?: string;
    beds?: number;
    baths?: number;
    sqft?: number;
    lotSize?: number;
    propertyType?: string;
  };
  context?: Record<string, unknown>;
  createdAt: string;
}

export const advisorSuggestionFeedback = {
  save: (data: AdvisorSuggestionFeedback) => api.post("/listing-interactions/advisor-suggestion-feedback", {
    ...data,
    feedbackId: data.feedbackId,
    listingId: Number(data.listingId),
  }),
};

export const geo = {
  nearbyCities: (city: string) => api.get("/geo/nearby-cities", { params: { city } }),
  boundary: (city: string, state?: string | null) => api.get<GeoBoundary>("/geo/boundary", { params: state ? { city, state } : { city } }),
};

export const savedSearches = {
  list: () => api.get<SavedSearchRecord[]>("/saved-searches/"),
  get: (id: number) => api.get<SavedSearchRecord>(`/saved-searches/${id}`),
  save: (data: Record<string, unknown>) => api.post<SavedSearchRecord>("/saved-searches/", data),
  update: (id: number, data: Record<string, unknown>) => api.patch<SavedSearchRecord>(`/saved-searches/${id}`, data),
  // Any subset of the five scalar criteria, through the server-side owner: a key
  // sent as null clears that field, a key not sent is left alone. Nothing else
  // on the record is touched, unlike `update`, which replaces the whole row.
  updateCriteria: (id: number, data: SavedSearchCriteriaPatch) => api.patch<SavedSearchRecord>(`/saved-searches/${id}/criteria`, data),
  // Add and/or remove towns without re-sending the row ("Merrick, NY" adds a town
  // in its own state). The server validates against the sync list.
  updateTowns: (id: number, data: { add?: string[]; remove?: string[] }) => api.patch<SavedSearchRecord>(`/saved-searches/${id}/towns`, data),
  updateAlertSettings: (id: number, data: { alert_frequency?: "daily" | "instant"; alert_min_score?: number; include_near_matches?: boolean }) =>
    api.patch(`/saved-searches/${id}/alert-settings`, data),
  delete: (id: number) => api.delete(`/saved-searches/${id}`),
  toggleAlert: (id: number) => api.patch(`/saved-searches/${id}/toggle-alert`),
};

export type SavedSearchCriteriaPatch = {
  property_type?: string | null;
  min_beds?: number | null;
  min_baths?: number | null;
  min_price?: number | null;
  max_price?: number | null;
};

export interface SavedSearchRecord {
  [key: string]: unknown;
  id: number;
  name: string;
  raw_query: string | null;
  criteria: Record<string, unknown>;
  filters: Record<string, unknown>;
  sort_by?: string | null;
  ai_filters: Array<Record<string, unknown>>;
  alert_enabled: boolean;
  alert_frequency: "daily" | "instant" | string;
  alert_min_score: number;
  include_near_matches: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  last_checked_at?: string | null;
  last_alert_sent_at?: string | null;
  new_matches_count?: number | null;
  alert_health?: Record<string, unknown> | null;
  // Bare towns that match nothing because they are in another state (lib/unmatched-towns).
  unmatched_towns?: UnmatchedTown[];
}

export const aiPicks = {
  get: (query: string, listing_ids: number[], liked_ids?: number[], disliked_ids?: number[], context?: Record<string, unknown>, scope?: { towns?: string[]; max_price?: number | null; min_beds?: number | null; close_call_ids?: number[]; saved_ai_filters?: string[] }) =>
    api.post("/ai-picks/", {
      query, listing_ids, liked_ids, disliked_ids, context,
      towns: scope?.towns, max_price: scope?.max_price ?? undefined, min_beds: scope?.min_beds ?? undefined,
      close_call_ids: scope?.close_call_ids, saved_ai_filters: scope?.saved_ai_filters,
    }),
  insight: (listing_id: number, query?: string) =>
    api.post("/ai-picks/insight", { listing_id, query }),
  diagnose: (query: string, criteria: Record<string, unknown>) =>
    api.post("/ai-picks/no-results", { query, criteria }),
};

export interface AdvisorSavedPreference {
  kind: string;
  value: string;
  label: string;
}

export type AdvisorActionType =
  | "remove_memory_signal"
  | "add_preference"
  | "mark_preference_avoid"
  | "mark_preference_must_have"
  | "mark_preference_nice_to_have"
  | "add_ai_filter"
  | "remove_ai_filter"
  | "update_ai_filter"
  | "update_ai_filter_unknowns"
  | "save_preference"
  | "remove_preference"
  | "change_alert_mode"
  | "change_alert_frequency"
  | "update_watch_criteria"
  | "remove_town"
  | "save_listing"
  | "hide_listing"
  | "watch_listing"
  | "create_tour_shortlist"
  | "create_agent_question_list"
  | "create_tour_checklist"
  | "update_watch_mode"
  | "suggest_search_filter_update";

export interface AdvisorActionProposal {
  action_id?: string;
  type?: AdvisorActionType | string;
  label?: string;
  summary?: string;
  requires_confirmation?: boolean;
  risk_level?: "low" | "medium" | "high" | string;
  current_state?: Record<string, unknown>;
  proposed_state?: Record<string, unknown>;
  diff?: Array<Record<string, unknown>>;
  recommendation?: string;
  warning_copy?: string;
  execution_options?: Array<Record<string, unknown>>;
  reason?: string;
  source?: string;
  surface?: string;
  expires_at?: string | null;
  safety?: Record<string, unknown>;
}

export interface AdvisorActionResult {
  action: AdvisorActionType;
  payload: Record<string, unknown>;
  status: "completed" | "needs_confirmation" | "failed" | "suggested";
  confirmation_required: boolean;
  message: string;
  proposal?: AdvisorActionProposal;
  action_id?: string;
  type?: AdvisorActionType | string;
  label?: string;
  summary?: string;
  requires_confirmation?: boolean;
  risk_level?: "low" | "medium" | "high" | string;
  current_state?: Record<string, unknown>;
  proposed_state?: Record<string, unknown>;
  reason?: string;
  safety?: Record<string, unknown>;
}

export type AdvisorChipType =
  | "prompt"
  | "search"
  | "open_listing"
  | "save_listing"
  | "hide_listing"
  | "feedback"
  | "compare"
  | "watch"
  | "tour";

export interface AdvisorChip {
  label: string;
  type: AdvisorChipType;
  prompt?: string | null;
  payload?: Record<string, unknown>;
}

// B-Listing-Card-Hydration: the backend now ships hydrated structured data for every
// /listing/{N} token in the answer so the frontend can render listing-reference card
// headers with the actual address instead of falling back to "Saved home". Shape
// mirrors the AdvisorListingCandidate Pydantic model on the backend (advisor_service.py).
// Kept structurally compatible with the existing AdvisorListingCandidate in
// advisor-listing-references.ts — the assistant-profile page merges both sources
// (this per-message hydration + opening-signal-derived candidates) into the candidate
// set passed to AdvisorAnswerContent.
export interface AdvisorListingCandidate {
  id: number;
  address: string;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  price?: number | null;
  beds?: number | null;
  baths?: number | null;
  sqft?: number | null;
  display_status?: string | null;
  // How the home sits against the buyer's search ("Outside your towns", "Stretch:
  // $400k over your max"), from the backend's reply check. Empty when it fits.
  fit_labels?: string[] | null;
}

export interface AdvisorResponse {
  answer: string;
  suggested_questions: string[];
  chips?: AdvisorChip[];
  suggested_search?: string | null;
  saved_preferences: AdvisorSavedPreference[];
  intent: string;
  auto_filter?: string | null;
  mode?: AdvisorMode;
  conversation_id?: number | null;
  message_id?: number | null;
  actions?: AdvisorActionResult[];
  listing_candidates?: AdvisorListingCandidate[];
  // Structured references the backend resolved for context (page + visible/comparable),
  // independent of the prose. Frontend street-matches these so named homes link reliably.
  answer_listing_references?: AdvisorListingCandidate[];
  // The homes this reply recommends, in its order: the only ones that get a card.
  recommended_listing_ids?: number[] | null;
  debug?: Record<string, unknown> | null;
}

export type AdvisorMode = "evaluate_home" | "refine_search" | "watch_market" | "remember_preferences" | "prepare_next_step";
export interface HomeAdvisorContextRequest {
  surface?: string;
  mode?: string;
  question?: string | null;
  listing_id?: number | null;
  visible_listing_ids?: number[];
  saved_search_id?: number | null;
  watch_id?: number | null;
  conversation_id?: number | null;
  active_search?: Record<string, unknown>;
  map_scope?: Record<string, unknown>;
  hard_criteria?: Record<string, unknown>;
  ai_filters?: Array<Record<string, unknown>>;
  local_listing_actions?: Record<string, unknown>;
}

export interface HomeAdvisorContextResponse {
  context: Record<string, unknown>;
  prompt_summary: string;
}

export interface ConversationMessageItem {
  id: number;
  role: "user" | "assistant";
  content: string;
  listing_id: number | null;
  created_at: string;
}

export type AdvisorThreadSurface = "general" | "home" | "results" | "listing" | "saved_search" | "watch" | "profile_memory";

export interface AdvisorThreadMessageItem {
  id: number;
  role: "user" | "assistant" | "system";
  content: string;
  created_at: string;
  metadata: Record<string, unknown>;
}

export interface AdvisorConversationItem {
  id: number;
  user_id: number;
  surface: AdvisorThreadSurface | string;
  listing_id: number | null;
  search_id: number | null;
  saved_search_id: number | null;
  watch_id: number | null;
  title: string | null;
  summary: string | null;
  context_label: string | null;
  message_count: number;
  last_message_preview: string | null;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  archived_at: string | null;
  deleted_at: string | null;
}

export interface AdvisorConversationDetail extends AdvisorConversationItem {
  messages: AdvisorThreadMessageItem[];
}

export type AdvisorMessageFeedbackRating = "helpful" | "not_helpful";
export type AdvisorMessageFeedbackReason =
  | "helpful"
  | "useful_answer"
  | "used_context"
  | "clear_next_step"
  | "good_recommendation"
  | "good_tone"
  | "too_long"
  | "wrong_intent"
  | "ignored_budget"
  | "ignored_listing"
  | "ignored_context"
  | "too_generic"
  | "no_clear_next_step"
  | "raw_memory_artifacts"
  | "made_something_up"
  | "bad_tone"
  | "not_practical"
  | "needs_adjustment"
  | "missed_monthly_payment"
  | "weak_town_tradeoff"
  | "missed_commute_constraint"
  | "ignored_school_uncertainty"
  | "missed_inventory_freshness"
  | "overconfident_market_claim"
  | "bad_search_widening_advice"
  | "missed_listing_risk"
  | "failed_to_compare_options";

export interface AdvisorMessageFeedback {
  rating: AdvisorMessageFeedbackRating;
  reasons: AdvisorMessageFeedbackReason[] | string[];
  note?: string | null;
  improvement?: string | null;
  source?: string | null;
  status?: "active" | "undone" | (string & {}) | null;
  created_at?: string | null;
  updated_at?: string | null;
  undone_at?: string | null;
  context?: Record<string, unknown>;
}

export interface AdvisorMessageFeedbackPayload {
  rating: AdvisorMessageFeedbackRating;
  reasons?: AdvisorMessageFeedbackReason[] | string[];
  note?: string | null;
  improvement?: string | null;
  context?: Record<string, unknown>;
}

export interface AdvisorMessageFeedbackResponse {
  message_id: number;
  feedback: AdvisorMessageFeedback;
}

export type AdvisorMessageRequestOptions = {
  surface?: "listing_detail" | "search_results" | "results" | "listing" | "saved_search" | "watch_alert" | "watch" | "profile_memory" | "general" | "home";
  search_id?: number | null;
  saved_search_id?: number | null;
  watch_id?: number | null;
  conversation_id?: number | null;
  force_new_conversation?: boolean;
  title?: string | null;
  context_snapshot?: Record<string, unknown>;
  skip_history?: boolean;
  opening_signals_snapshot?: AdvisorOpeningSignal[] | null;
  // The briefing sentence the page showed above the cards, so the backend can keep
  // it in the conversation and Mae knows what "them" refers to.
  briefing_text?: string | null;
};

export function buildAdvisorMessagePayload(
  question: string,
  listing_id?: number,
  context?: string,
  mode?: AdvisorMode,
  options?: AdvisorMessageRequestOptions,
) {
  return {
    question,
    listing_id: listing_id ?? null,
    context: context ?? null,
    mode: mode ?? null,
    surface: options?.surface ?? null,
    search_id: options?.search_id ?? null,
    saved_search_id: options?.saved_search_id ?? null,
    watch_id: options?.watch_id ?? null,
    conversation_id: options?.conversation_id ?? null,
    force_new_conversation: options?.force_new_conversation ?? false,
    title: options?.title ?? null,
    context_snapshot: options?.context_snapshot ?? {},
    skip_history: options?.skip_history ?? false,
    opening_signals_snapshot: options?.opening_signals_snapshot ?? null,
    briefing_text: options?.briefing_text ?? null,
  };
}

export const advisor = {
  message: (
    question: string,
    listing_id?: number,
    context?: string,
    mode?: AdvisorMode,
    options?: AdvisorMessageRequestOptions,
  ) =>
    api.post<AdvisorResponse>("/advisor/message", buildAdvisorMessagePayload(question, listing_id, context, mode, options), {
      timeout: ADVISOR_MESSAGE_TIMEOUT_MS,
    }),
  streamMessage: async (
    question: string,
    listing_id: number | undefined,
    context: string | undefined,
    mode: AdvisorMode | undefined,
    options: AdvisorMessageRequestOptions | undefined,
    handlers: {
      onEvent?: (event: AdvisorStreamEvent) => void;
      onStart?: (event: Extract<AdvisorStreamEvent, { type: "start" }>) => void;
      onDelta?: (event: Extract<AdvisorStreamEvent, { type: "delta" }>) => void;
      onFinal?: (event: Extract<AdvisorStreamEvent, { type: "final" }>) => void;
      onError?: (event: Extract<AdvisorStreamEvent, { type: "error" }>) => void;
    } = {},
  ) => {
    const payload = buildAdvisorMessagePayload(question, listing_id, context, mode, options);
    const buildHeaders = async () => {
      const headers = new Headers({
        "Content-Type": "application/json",
        Accept: "application/x-ndjson",
      });
      const bearerToken = getStoredAccessToken();
      if (bearerToken) {
        headers.set("Authorization", `Bearer ${bearerToken}`);
      } else {
        const token = await ensureCsrfToken();
        if (token) headers.set("X-CSRF-Token", token);
      }
      return headers;
    };

    const doFetch = async (forceCsrf = false) => {
      const headers = await buildHeaders();
      if (forceCsrf) {
        clearCsrfCookie();
        const token = await ensureCsrfToken(true);
        if (token) headers.set("X-CSRF-Token", token);
      }
      return fetch(`${api.defaults.baseURL}/advisor/message/stream`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(payload),
      });
    };

    let response = await doFetch();
    if (response.status === 403) {
      let detail = "";
      try {
        const data = await response.clone().json() as { detail?: unknown };
        detail = coerceDetailMessage(data.detail);
      } catch {}
      if (detail === "Invalid CSRF token") {
        response = await doFetch(true);
      }
    }
    if (!response.ok || !response.body) {
      let detail = "";
      try {
        const data = await response.clone().json() as { detail?: unknown };
        detail = coerceDetailMessage(data.detail);
      } catch {}
      // 429 = per-IP rate limit. Surface a friendly cooldown message (the backend
      // sends one as `detail`; fall back to a literal so it's friendly even if the
      // body is missing) rather than a generic "having trouble" error.
      if (response.status === 429) {
        throw new Error(detail || "You’re sending messages a little fast — give me a moment and try again.");
      }
      throw new Error(detail || `Stream request failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const parsed = parseAdvisorStreamBuffer(buffer);
      buffer = parsed.remainder;
      for (const event of parsed.events) {
        handlers.onEvent?.(event);
        if (event.type === "start") handlers.onStart?.(event);
        if (event.type === "delta") handlers.onDelta?.(event);
        if (event.type === "final") handlers.onFinal?.(event);
        if (event.type === "error") handlers.onError?.(event);
      }
      if (done) break;
    }
    if (buffer.trim()) {
      const parsed = parseAdvisorStreamBuffer(`${buffer}\n`);
      for (const event of parsed.events) {
        handlers.onEvent?.(event);
        if (event.type === "start") handlers.onStart?.(event);
        if (event.type === "delta") handlers.onDelta?.(event);
        if (event.type === "final") handlers.onFinal?.(event);
        if (event.type === "error") handlers.onError?.(event);
      }
    }
  },
  conversations: (params?: { limit?: number; include_archived?: boolean; include_legacy?: boolean }) =>
    api.get<AdvisorConversationItem[]>("/advisor/conversations", { params }),
  conversation: (conversationId: number) =>
    api.get<AdvisorConversationDetail>(`/advisor/conversations/${conversationId}`),
  feedback: (messageId: number, data: AdvisorMessageFeedbackPayload) =>
    api.post<AdvisorMessageFeedbackResponse>(`/advisor/messages/${messageId}/feedback`, data),
  undoFeedback: (messageId: number) =>
    api.delete<AdvisorMessageFeedbackResponse>(`/advisor/messages/${messageId}/feedback`),
  createConversation: (data: {
    surface?: AdvisorThreadSurface | string | null;
    listing_id?: number | null;
    search_id?: number | null;
    saved_search_id?: number | null;
    watch_id?: number | null;
    title?: string | null;
    context_snapshot?: Record<string, unknown>;
  }) => api.post<AdvisorConversationItem>("/advisor/conversations", data),
  // Find-or-create THIS surface's conversation (by the same context keys /advisor/message
  // resolves on) and return it with its messages — so a surface can load exactly the
  // conversation its sends will write to. Returns the same detail shape as conversation(id).
  resolveConversation: (data: {
    surface?: AdvisorThreadSurface | string | null;
    listing_id?: number | null;
    search_id?: number | null;
    saved_search_id?: number | null;
    watch_id?: number | null;
    conversation_id?: number | null;
    mode?: string | null;
    title?: string | null;
    context_snapshot?: Record<string, unknown>;
  }) => api.post<AdvisorConversationDetail>("/advisor/conversations/resolve", data),
  history: () =>
    api.get<ConversationMessageItem[]>("/advisor/history"),
  context: (data: HomeAdvisorContextRequest) =>
    api.post<HomeAdvisorContextResponse>("/advisor/context", data),
  openingMessage: (timezoneName?: string | null, force = false) =>
    api.get<AdvisorOpeningResponse>("/advisor/opening-message", {
      params: { ...(timezoneName ? { timezone: timezoneName } : {}), ...(force ? { force: true } : {}) },
    }),
  dailyBrief: () => api.get<DailyBriefResponse>("/advisor/daily-brief"),
  listingTake: (listingId: number) =>
    api.post<AdvisorListingTakeResponse>("/advisor/listing-take", { listing_id: listingId }),
};

export type AdvisorOpeningSignalType =
  | "new_listing_match"
  | "nearby_match"
  | "stretch_match"
  | "price_drop"
  | "saved_home_change"
  | "saved_home_concern"
  | "pattern_observed"
  | "market_shift"
  | "preference_needs_review";

export type AdvisorOpeningSignalUrgency = "high" | "medium" | "low";
export type AdvisorOpeningBranch = "quiet" | "focused" | "briefing" | "triage";

export interface AdvisorOpeningSignal {
  id: string;
  type: AdvisorOpeningSignalType;
  subject: Record<string, unknown>;
  urgency: AdvisorOpeningSignalUrgency;
  summary: string;
  chip_label: string;
  prompt: string;
  created_at: string;
  score: number;
}

export interface AdvisorOpeningChip {
  label: string;
  prompt: string;
  signal_id?: string | null;
}

export type MayNoticedAction = "confirm" | "reject" | "later" | "explain";

export interface MayNoticedCandidate {
  id: string;
  observation: string;
  question: string;
  why: string;
  candidate_preference: {
    kind: PreferenceProfileSignalKind;
    value: string;
    label: string;
    confidence: "medium" | "high";
    suggested_action: Extract<PreferenceCorrectionAction, "mark_prefer" | "mark_avoid">;
  };
  evidence: Array<{ label: string; detail?: string | null }>;
  actions: MayNoticedAction[];
}

// Lightweight, counts-only market digest carried on the opening response. The
// whole object may be null (old backend, cache miss, or a backend error), and
// every field is optional — consumers must degrade gracefully when it's absent.
export interface MarketDigest {
  active_matches?: number | null;
  new_listings?: number | null;
  price_drops?: number | null;
  saved_home_changes?: number | null;
  lookback_days?: number | null;
  last_checked_at?: string | null;
  // The hard criteria the counts actually filtered on (saved search merged
  // stricter-wins with explicit memory, plus the scanned towns), and the subset
  // explicit memory contributed beyond the saved search. Both optional: an older
  // backend or cached payload omits them, which reads as "nothing to say".
  criteria?: Record<string, unknown> | null;
  memory_criteria?: Record<string, unknown> | null;
}

// The alert run's last look at the buyer's active watch (backend WatchCheck). The
// time and counts are the latest check that COMPLETED; the status is the latest
// attempt. Absent when the buyer has no watch, and on the error fallback.
export interface WatchCheck {
  saved_search_id: number;
  last_checked_at?: string | null;
  last_check_status?: "found" | "quiet" | "stale" | "failed" | string | null;
  last_check_counts?: { evaluated?: number; new?: number; price_changes?: number } | null;
  // Where that check's window started (backend watch_check.check_window): the previous
  // check, or at most 7 days back. A gap of more than a day is said plainly.
  last_check_since?: string | null;
  next_check_at?: string | null;
}

export interface AdvisorOpeningResponse {
  // When the response was built: the page load, NOT a check. Never shown as one.
  generated_at: string;
  branch: AdvisorOpeningBranch;
  message: string;
  chips: AdvisorOpeningChip[];
  signals: AdvisorOpeningSignal[];
  has_signal: boolean;
  unread_updates: boolean;
  last_visit_at?: string | null;
  // The instant "what's new" counts from (the buyer's previous visit, at most seven
  // days back, else 24h) and the timezone to say it in.
  window_since_at?: string | null;
  window_timezone?: string | null;
  may_noticed?: MayNoticedCandidate | null;
  market_digest?: MarketDigest | null;
  watch_check?: WatchCheck | null;
  cached?: boolean;
}

export type AdvisorListingTakeVerdict = "tour" | "watch" | "skip" | "undecided";

export interface AdvisorListingTakeResponse {
  what_i_like: string;
  concerns: string;
  questions_for_agent: string[];
  verdict: AdvisorListingTakeVerdict;
  generated_at: string;
}

export type DailyBriefHighlightKind =
  | "new_listings"
  | "price_drops"
  | "status_changes"
  | "open_houses"
  | "saved_home_changes";

export interface DailyBriefHighlightListing {
  listing_id: number;
  address?: string | null;
  city?: string | null;
  price?: number | null;
  previous_price?: number | null;
  path: string;
  reason?: string | null;
}

export interface DailyBriefHighlight {
  kind: DailyBriefHighlightKind;
  title: string;
  detail?: string | null;
  cta_label: string;
  cta_prompt: string;
  listings: DailyBriefHighlightListing[];
}

export interface DailyBriefObservation {
  text: string;
  suggested_prompt?: string | null;
}

export interface DailyBriefChip {
  label: string;
  prompt: string;
}

export interface DailyBriefResponse {
  generated_at: string;
  greeting: string;
  has_signal: boolean;
  has_saved_search?: boolean;
  scope_label?: string | null;
  highlights: DailyBriefHighlight[];
  observation?: DailyBriefObservation | null;
  quiet_note?: string | null;
  chips: DailyBriefChip[];
}

export type DecisionMemoryType =
  | "saved_home"
  | "hidden_home"
  | "watched_home"
  | "compared_home"
  | "good_example"
  | "not_my_style"
  | "asked_affordability"
  | "asked_red_flags"
  | "drafted_agent_questions"
  | "commute_destination_added"
  | "search_criteria_adjusted"
  | "alert_mode_changed"
  | "preference_corrected";

export interface AdvisorDecisionMemoryItem {
  id: number;
  decision_type: DecisionMemoryType;
  listing_id?: number | null;
  saved_search_id?: number | null;
  watch_id?: number | null;
  structured_signal?: Record<string, unknown>;
  confidence?: "low" | "medium" | "high";
  memory_source?: "explicit" | "inferred";
  note?: string | null;
  created_at?: string | null;
}

export interface AdvisorDecisionMemorySummary {
  total: number;
  explicit_count: number;
  inferred_count: number;
  decision_counts: Record<string, number>;
  top_cities: Array<{ value: string; count: number }>;
  top_property_types: Array<{ value: string; count: number }>;
  listing_patterns: Array<{ listing_id: number; decisions: string[]; count: number }>;
  recent: AdvisorDecisionMemoryItem[];
}

export const advisorMemory = {
  rememberDecision: (data: {
    decision_type: DecisionMemoryType;
    listing_id?: number | null;
    saved_search_id?: number | null;
    watch_id?: number | null;
    structured_signal?: Record<string, unknown>;
    confidence?: "low" | "medium" | "high";
    memory_source?: "explicit" | "inferred";
    note?: string | null;
  }) => api.post("/advisor/memory/decisions", data),
  summary: (limit?: number) => api.get<AdvisorDecisionMemorySummary>("/advisor/memory/summary", { params: limit ? { limit } : undefined }),
  correct: (memoryId: number, action: "remove" | "dismiss" | "wrong" | "mark_wrong" | "dont_remember") =>
    api.post(`/advisor/memory/decisions/${memoryId}/correct`, { action }),
  edit: (memoryId: number, data: {
    value?: string | null;
    label?: string | null;
    note?: string | null;
    action?: PreferenceCorrectionAction | "remove" | "dismiss" | "wrong" | "mark_wrong" | "dont_remember" | null;
  }) => api.patch(`/advisor/memory/decisions/${memoryId}`, data),
};

export interface RealtorTourRequest {
  preferred_dates: string[];        // ISO "YYYY-MM-DD" preferred days
  time_of_day?: string | null;      // "morning" | "afternoon" | "evening"
  note?: string | null;
}

export interface RealtorReferralPayload {
  listing_id?: number | null;
  source?: "listing_quick_view" | "listing_detail" | "home_advisor" | "contact_agent" | "tour_request";
  name: string;
  email: string;
  phone?: string | null;
  message?: string | null;
  timeline?: string | null;
  tour_request?: RealtorTourRequest | null;
}

export const realtorReferrals = {
  create: (data: RealtorReferralPayload) =>
    api.post<{ id: number; status: string; message: string; buyer_brief: Record<string, unknown> }>("/realtor-referrals/", data),
};

export const onboarding = {
  complete: (data: { commute_to?: string; priority?: string; max_budget?: number }) =>
    api.post<{ ok: boolean; signals_saved: number }>("/onboarding/complete", data),
};

export const evaluateFilter = {
  single: (filter_text: string, listing_id: number) =>
    api.post("/evaluate-filter/", { filter_text, listing_id }),
  batch: (filter_text: string, listing_ids: number[]) =>
    api.post("/evaluate-filter/batch", { filter_text, listing_ids }),
};
